import { effectScope, watchEffect, type Directive, type DirectiveBinding, type EffectScope } from 'vue'
import type { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'

/**
 * `v-permission="{ resource, level }"` — disables a button or link and shows a lock icon + tooltip when
 * the current internal employee lacks the given admin permission. Mirrors the portal's FeatureButton
 * behaviour. For menu items and other non-DOM controls use the {@link usePermissions} composable
 * instead (a directive can only reach a rendered element).
 *
 * The check is reactive (re-evaluates if admin access is (re)loaded) and re-applies on component update
 * so PrimeVue re-renders don't drop the injected lock icon. Write implies Read and a global admin
 * passes every check — both handled by the store.
 */
export type PermissionBinding = {
  resource: AdminPermissionResource
  level: PermissionLevel
  /** Overrides the default Dutch "no rights" tooltip. */
  tooltip?: string
}

const DEFAULT_TOOLTIP = 'Je hebt geen rechten voor deze actie.'
const LOCK_CLASS = 'perm-lock-icon'

type PermState = {
  scope: EffectScope
  binding: PermissionBinding
}

const registry = new WeakMap<HTMLElement, PermState>()

function ensureLockIcon(el: HTMLElement) {
  if (el.querySelector(`:scope > .${LOCK_CLASS}`)) {
    return
  }

  const icon = document.createElement('i')

  icon.className = `fa-regular fa-lock ${LOCK_CLASS}`
  icon.setAttribute('aria-hidden', 'true')
  icon.style.marginRight = '0.35em'
  el.prepend(icon)
}

function removeLockIcon(el: HTMLElement) {
  el.querySelector(`:scope > .${LOCK_CLASS}`)?.remove()
}

function apply(el: HTMLElement, locked: boolean, tooltip: string) {
  const isAnchor = el.tagName === 'A'

  if (locked) {
    el.setAttribute('title', tooltip)
    el.setAttribute('aria-disabled', 'true')

    if (isAnchor) {
      el.style.pointerEvents = 'none'
      el.style.opacity = '0.5'
      el.style.cursor = 'not-allowed'
    } else {
      // Native disabled greys the button (PrimeVue styling) and blocks its click handler.
      el.setAttribute('disabled', 'disabled')
      ;(el as HTMLButtonElement).disabled = true
    }

    ensureLockIcon(el)

    return
  }

  el.removeAttribute('title')
  el.removeAttribute('aria-disabled')

  if (isAnchor) {
    el.style.pointerEvents = ''
    el.style.opacity = ''
    el.style.cursor = ''
  } else {
    el.removeAttribute('disabled')
    ;(el as HTMLButtonElement).disabled = false
  }

  removeLockIcon(el)
}

function evaluate(el: HTMLElement) {
  const state = registry.get(el)

  if (!state) {
    return
  }

  const access = useAdminAccessStore()
  const allowed = access.hasPermission(state.binding.resource, state.binding.level)

  apply(el, !allowed, state.binding.tooltip ?? DEFAULT_TOOLTIP)
}

const vPermission: Directive<HTMLElement, PermissionBinding | undefined> = {
  mounted(el, binding: DirectiveBinding<PermissionBinding | undefined>) {
    if (!binding.value) {
      return
    }

    const scope = effectScope()

    registry.set(el, {
      scope,
      binding: binding.value, 
    })

    // watchEffect tracks the store's reactive access state, so the lock re-applies on (re)load.
    scope.run(() => watchEffect(() => evaluate(el)))
  },

  updated(el, binding: DirectiveBinding<PermissionBinding | undefined>) {
    const state = registry.get(el)

    if (!state || !binding.value) {
      return
    }

    state.binding = binding.value
    // Re-apply after a re-render (which may have removed the injected lock icon).
    evaluate(el)
  },

  unmounted(el) {
    registry.get(el)?.scope.stop()
    registry.delete(el)
  },
}

export default vPermission
